import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/api';
import { Draft, Team, User } from '../types';
import { 
  Container, 
  Card, 
  Button, 
  Input,
  Label,
  LoadingSpinner,
  colors,
  spacing,
  typography,
  media,
  shadows
} from '../styles/GlobalStyles';
import { 
  Settings, 
  Plus, 
  Users, 
  Trophy,
  Play,
  Trash2,
  UserPlus,
  Database,
  GripVertical,
  Save,
  ChevronUp,
  ChevronDown,
  BarChart3
} from 'lucide-react';
import toast from 'react-hot-toast';

const getTeamOrderValue = (team: Team): number => team.draftOrder || team.teamNumber;

const sortTeamsByDraftOrder = (teamList: Team[]): Team[] =>
  [...teamList].sort((a, b) => getTeamOrderValue(a) - getTeamOrderValue(b));

const AdminContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: #000000;
`;

const PageHeader = styled.div`
  margin-bottom: ${spacing[8]};
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[2]};
`;

const PageSubtitle = styled.p`
  font-size: ${typography.fontSize.lg};
  color: ${colors.grey[300]};
`;

const AdminGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[6]};
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  ${media.tablet} {
    gap: ${spacing[8]};
  }

  ${media.desktop} {
    grid-template-columns: 2fr 1fr;
  }
`;

const SectionCard = styled(Card)`
  padding: ${spacing[4]};
  width: 100%;
  max-width: 100%;
  overflow: hidden;

  ${media.tablet} {
    padding: ${spacing[6]};
  }
`;

const SectionTitle = styled.h2`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[4]};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};

  ${media.tablet} {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

const ActionCard = styled(Card)`
  padding: ${spacing[4]};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${colors.grey[800]};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.lg};
    border-color: ${colors.green};
  }
`;

const ActionIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${colors.green}20;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing[3]} auto;
  color: ${colors.green};
`;

const ActionTitle = styled.h3`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
`;

const ActionDescription = styled.p`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

const DraftsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const DraftItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #000;
  border: 1px solid #000000;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
  gap: ${spacing[3]};
  min-width: 0; /* Allow flex items to shrink */
  overflow: hidden; /* Prevent content overflow */

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const DraftInfo = styled.div`
  flex: 1;
  min-width: 0; /* Allow text to wrap/truncate */
  overflow: hidden;
`;

const DraftName = styled.div`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DraftDetails = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DraftActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  flex-shrink: 0; /* Prevent actions from shrinking */
  flex-wrap: wrap; /* Allow buttons to wrap on small screens */

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
    gap: ${spacing[1]};

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const TeamOrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[3]};
`;

const TeamOrderHint = styled.p`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
  margin: 0;
`;

const DraggableTeamItem = styled(DraftItem).withConfig({
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isDragOver'
})<{ isDragging: boolean; isDragOver: boolean }>`
  opacity: ${props => props.isDragging ? 0.55 : 1};
  border-color: ${props => props.isDragOver ? colors.green : '#000000'};
`;

const DragHandle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid ${colors.grey[700]};
  border-radius: ${spacing[2]};
  background: ${colors.grey[800]};
  color: ${colors.grey[300]};
  cursor: grab;
  flex-shrink: 0;

  &:active {
    cursor: grabbing;
  }
`;

const TeamOrderControls = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-right: ${spacing[2]};
`;

const OrderButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${colors.grey[700]};
  border-radius: ${spacing[2]};
  background: ${colors.grey[800]};
  color: ${colors.grey[300]};
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'status'
})<{ status: string }>`
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  margin-right: ${spacing[2]};
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: ${colors.green};
          color: ${colors.black};
          font-weight: ${typography.fontWeight.bold};
        `;
      case 'completed':
        return `
          background: ${colors.info}20;
          color: ${colors.info};
        `;
      default:
        return `
          background: ${colors.grey[700]};
          color: ${colors.grey[300]};
        `;
    }
  }}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[4]};
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  padding: 1rem;
  border-radius: 0.375rem;
  background: #030303;
  border: 1px solid #000000;
`;

const StatLabel = styled.span`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.sm};
`;

const StatValue = styled.span`
  color: ${colors.white};
  font-weight: ${typography.fontWeight.semibold};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const Modal = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing[4]};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 500px;
  padding: ${spacing[6]};

  @media (max-width: 767px) {
    max-width: 100%;
    padding: ${spacing[4]};
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const ModalTitle = styled.h3`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[4]};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${spacing[3]};
  justify-content: flex-end;
  margin-top: ${spacing[6]};

  @media (max-width: 767px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const TeamsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  max-height: 300px;
  overflow-y: auto;
  margin: ${spacing[4]} 0;
`;

const TeamItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: ${spacing[3]};
  background: ${props => props.selected ? colors.green + '20' : colors.grey[800]};
  border: 1px solid ${props => props.selected ? colors.green : colors.grey[700]};
  border-radius: ${spacing[2]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.selected ? colors.green + '30' : colors.grey[700]};
  }
`;

const AssignableTeamItem = styled(TeamItem).withConfig({
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isDragOver'
})<{ isDragging: boolean; isDragOver: boolean }>`
  opacity: ${props => props.isDragging ? 0.6 : 1};
  border-color: ${props => props.isDragOver ? colors.green : undefined};
`;

const TeamInfo = styled.div`
  flex: 1;
  margin-left: ${spacing[3]};
`;

const TeamName = styled.div`
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
`;

const TeamDetails = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${colors.green};
`;

const ReportsModalContent = styled(ModalContent)`
  max-width: 920px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ReportDraftList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[2]};
  margin: ${spacing[4]} 0;

  ${media.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ReportDraftOption = styled.label.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  min-width: 0;
  padding: ${spacing[3]};
  border: 1px solid ${props => props.selected ? colors.green : colors.grey[700]};
  border-radius: 0.375rem;
  background: ${props => props.selected ? `${colors.green}18` : colors.grey[900]};
  color: ${colors.white};
  cursor: pointer;
`;

const ReportDraftName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing[3]};
  margin: ${spacing[5]} 0;

  ${media.tablet} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const ReportMetric = styled.div`
  padding: ${spacing[3]};
  border: 1px solid ${colors.grey[700]};
  border-radius: 0.375rem;
  background: ${colors.grey[950]};
`;

const ReportMetricLabel = styled.div`
  color: ${colors.grey[400]};
  font-size: ${typography.fontSize.xs};
`;

const ReportMetricValue = styled.div`
  color: ${colors.white};
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  margin-top: ${spacing[1]};
`;

const ReportSectionTitle = styled.h4`
  color: ${colors.white};
  font-size: ${typography.fontSize.base};
  margin: ${spacing[5]} 0 ${spacing[2]};
`;

const ReportList = styled.div`
  border-top: 1px solid ${colors.grey[800]};
`;

const ReportRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${spacing[3]};
  padding: ${spacing[2]} 0;
  border-bottom: 1px solid ${colors.grey[800]};
  color: ${colors.grey[300]};
  font-size: ${typography.fontSize.sm};
`;

const ReportPrimary = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AdminPage: React.FC = () => {
  // const { user } = useAuth();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [orderedTeams, setOrderedTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDraftModal, setShowCreateDraftModal] = useState(false);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [showDeleteDraftModal, setShowDeleteDraftModal] = useState(false);
  const [showAssignTeamsModal, setShowAssignTeamsModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<Draft | null>(null);
  const [draftToAssignTeams, setDraftToAssignTeams] = useState<Draft | null>(null);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [assignTeamOrderIds, setAssignTeamOrderIds] = useState<string[]>([]);
  const [assignDraggingTeamId, setAssignDraggingTeamId] = useState<string | null>(null);
  const [assignDragOverTeamId, setAssignDragOverTeamId] = useState<string | null>(null);
  const [newDraftName, setNewDraftName] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamNumber, setNewTeamNumber] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [assigningTeams, setAssigningTeams] = useState(false);
  const [savingTeamOrder, setSavingTeamOrder] = useState(false);
  const [runningReports, setRunningReports] = useState(false);
  const [selectedReportDraftIds, setSelectedReportDraftIds] = useState<string[]>([]);
  const [reportDrafts, setReportDrafts] = useState<Draft[]>([]);
  const [isTeamOrderDirty, setIsTeamOrderDirty] = useState(false);
  const [draggingTeamId, setDraggingTeamId] = useState<string | null>(null);
  const [dragOverTeamId, setDragOverTeamId] = useState<string | null>(null);
  
  // User assignment state
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [teamToAssignUser, setTeamToAssignUser] = useState<Team | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [assigningUser, setAssigningUser] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isTeamOrderDirty) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isTeamOrderDirty]);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [draftsResponse, teamsResponse, usersResponse] = await Promise.all([
        apiClient.drafts.getAll(),
        apiClient.teams.getAll(),
        apiClient.users.getAll()
      ]);

      if (draftsResponse.success) {
        setDrafts(draftsResponse.data.drafts);
      }
      if (teamsResponse.success) {
        const sortedTeams = sortTeamsByDraftOrder(teamsResponse.data.teams);
        setTeams(sortedTeams);
        setOrderedTeams(sortedTeams);
        setIsTeamOrderDirty(false);
      }
      if (usersResponse.success) {
        setUsers(usersResponse.data.users);
      }
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiClient.drafts.create({
        name: newDraftName,
        settings: {
          teamsCount: 12,
          playersPerTeam: 20,
          budgetPerTeam: 260,
          draftType: 'auction'
        }
      });

      if (response.success) {
        toast.success('Draft created successfully');
        setNewDraftName('');
        setShowCreateDraftModal(false);
        loadAdminData();
      }
    } catch (error) {
      console.error('Failed to create draft:', error);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await apiClient.teams.create({
        name: newTeamName,
        teamNumber: parseInt(newTeamNumber)
      });

      if (response.success) {
        toast.success('Team created successfully');
        setNewTeamName('');
        setNewTeamNumber('');
        setShowCreateTeamModal(false);
        loadAdminData();
      }
    } catch (error) {
      console.error('Failed to create team:', error);
    }
  };

  const handleStartDraft = async (draftId: string) => {
    try {
      const response = await apiClient.drafts.start(draftId);
      if (response.success) {
        toast.success('Draft started successfully');
        loadAdminData();
      }
    } catch (error) {
      console.error('Failed to start draft:', error);
    }
  };

  const handleSyncPlayers = async () => {
    try {
      setSyncing(true);
      const response = await apiClient.players.syncFromSportsDataIO();
      if (response.success) {
        const payload = response.data || {};
        const updatedPlayers = payload.updatedPlayers ?? response.updatedPlayers ?? 0;
        const source = payload.source || 'SportsDataIO';
        const season = payload.season || 'current';
        toast.success(`Player sync completed: ${updatedPlayers} players updated (${source}, ${season})`);
      }
    } catch (error: any) {
      console.error('Failed to sync players:', error);
      const serverMessage = error?.response?.data?.message;
      if (serverMessage) {
        toast.error(`Failed to sync players: ${serverMessage}`);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteDraft = (draft: Draft) => {
    setDraftToDelete(draft);
    setShowDeleteDraftModal(true);
  };

  const confirmDeleteDraft = async () => {
    if (!draftToDelete) return;

    try {
      setDeleting(true);
      const response = await apiClient.drafts.delete(draftToDelete._id);
      if (response.success) {
        toast.success('Draft deleted successfully');
        setShowDeleteDraftModal(false);
        setDraftToDelete(null);
        loadAdminData();
      }
    } catch (error) {
      console.error('Failed to delete draft:', error);
      toast.error('Failed to delete draft');
    } finally {
      setDeleting(false);
    }
  };

  const handleAssignTeams = (draft: Draft) => {
    const draftTeamIds = draft.teams.map((team) => team._id) || [];
    const remainingTeamIds = orderedTeams
      .map((team) => team._id)
      .filter((teamId) => !draftTeamIds.includes(teamId));

    setDraftToAssignTeams(draft);
    setSelectedTeamIds(draftTeamIds);
    setAssignTeamOrderIds([...draftTeamIds, ...remainingTeamIds]);
    setAssignDraggingTeamId(null);
    setAssignDragOverTeamId(null);
    setShowAssignTeamsModal(true);
  };

  const handleAssignTeamDragStart = (teamId: string) => {
    setAssignDraggingTeamId(teamId);
  };

  const handleAssignTeamDragOver = (event: React.DragEvent<HTMLDivElement>, teamId: string) => {
    event.preventDefault();
    if (!assignDraggingTeamId || assignDraggingTeamId === teamId) {
      return;
    }
    setAssignDragOverTeamId(teamId);
  };

  const handleAssignTeamDrop = (targetTeamId: string) => {
    if (!assignDraggingTeamId || assignDraggingTeamId === targetTeamId) {
      setAssignDraggingTeamId(null);
      setAssignDragOverTeamId(null);
      return;
    }

    setAssignTeamOrderIds((prevTeamIds) => {
      const fromIndex = prevTeamIds.findIndex((teamId) => teamId === assignDraggingTeamId);
      const toIndex = prevTeamIds.findIndex((teamId) => teamId === targetTeamId);

      if (fromIndex < 0 || toIndex < 0) {
        return prevTeamIds;
      }

      const reorderedTeamIds = [...prevTeamIds];
      const [movedTeamId] = reorderedTeamIds.splice(fromIndex, 1);
      reorderedTeamIds.splice(toIndex, 0, movedTeamId);
      return reorderedTeamIds;
    });

    setAssignDraggingTeamId(null);
    setAssignDragOverTeamId(null);
  };

  const handleAssignTeamDragEnd = () => {
    setAssignDraggingTeamId(null);
    setAssignDragOverTeamId(null);
  };

  const handleTeamDragStart = (teamId: string) => {
    setDraggingTeamId(teamId);
  };

  const handleTeamDragOver = (event: React.DragEvent<HTMLDivElement>, teamId: string) => {
    event.preventDefault();
    if (!draggingTeamId || draggingTeamId === teamId) {
      return;
    }
    setDragOverTeamId(teamId);
  };

  const handleTeamDrop = (targetTeamId: string) => {
    if (!draggingTeamId || draggingTeamId === targetTeamId) {
      setDragOverTeamId(null);
      setDraggingTeamId(null);
      return;
    }

    setOrderedTeams((prevTeams) => {
      const fromIndex = prevTeams.findIndex((team) => team._id === draggingTeamId);
      const toIndex = prevTeams.findIndex((team) => team._id === targetTeamId);

      if (fromIndex < 0 || toIndex < 0) {
        return prevTeams;
      }

      const reorderedTeams = [...prevTeams];
      const [movedTeam] = reorderedTeams.splice(fromIndex, 1);
      reorderedTeams.splice(toIndex, 0, movedTeam);

      setIsTeamOrderDirty(true);
      return reorderedTeams.map((team, index) => ({
        ...team,
        draftOrder: index + 1
      }));
    });

    setDragOverTeamId(null);
    setDraggingTeamId(null);
  };

  const handleTeamDragEnd = () => {
    setDragOverTeamId(null);
    setDraggingTeamId(null);
  };

  const moveTeamByDirection = (teamId: string, direction: 'up' | 'down') => {
    setOrderedTeams((prevTeams) => {
      const currentIndex = prevTeams.findIndex((team) => team._id === teamId);
      if (currentIndex < 0) {
        return prevTeams;
      }

      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= prevTeams.length) {
        return prevTeams;
      }

      const reorderedTeams = [...prevTeams];
      const [movedTeam] = reorderedTeams.splice(currentIndex, 1);
      reorderedTeams.splice(targetIndex, 0, movedTeam);

      setIsTeamOrderDirty(true);
      return reorderedTeams.map((team, index) => ({
        ...team,
        draftOrder: index + 1
      }));
    });
  };

  const handleSaveTeamOrder = async () => {
    if (!isTeamOrderDirty || orderedTeams.length === 0) {
      return;
    }

    try {
      setSavingTeamOrder(true);
      const response = await apiClient.teams.reorder(orderedTeams.map((team) => team._id));
      if (response.success) {
        const updatedTeams = sortTeamsByDraftOrder(response.data.teams || orderedTeams);
        setTeams(updatedTeams);
        setOrderedTeams(updatedTeams);
        setIsTeamOrderDirty(false);
        toast.success('Team draft order saved');
      }
    } catch (error: any) {
      console.error('Failed to save team draft order:', error);
      const serverMessage = error?.response?.data?.message;
      toast.error(serverMessage ? `Failed to save team order: ${serverMessage}` : 'Failed to save team order');
    } finally {
      setSavingTeamOrder(false);
    }
  };

  const handleTeamSelection = (teamId: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedTeamIds(prev => [...prev, teamId]);
    } else {
      setSelectedTeamIds(prev => prev.filter(id => id !== teamId));
    }
  };

  const confirmAssignTeams = async () => {
    if (!draftToAssignTeams) return;

    const orderedSelectedTeamIds = assignTeamOrderIds.filter((teamId) => selectedTeamIds.includes(teamId));

    try {
      setAssigningTeams(true);
      const response = await apiClient.drafts.addTeams(draftToAssignTeams._id, orderedSelectedTeamIds);
      if (response.success) {
        toast.success('Teams assigned successfully');
        setShowAssignTeamsModal(false);
        setDraftToAssignTeams(null);
        setSelectedTeamIds([]);
        loadAdminData();
      }
    } catch (error: any) {
      console.error('Failed to assign teams:', error);
      const serverMessage = error?.response?.data?.message;
      toast.error(serverMessage ? `Failed to assign teams: ${serverMessage}` : 'Failed to assign teams');
    } finally {
      setAssigningTeams(false);
    }
  };

  const handleAssignUser = (team: Team) => {
    setTeamToAssignUser(team);
    setSelectedUserId(team.ownerId?._id || '');
    setShowAssignUserModal(true);
  };

  const toggleReportDraft = (draftId: string) => {
    setSelectedReportDraftIds((previous) => (
      previous.includes(draftId)
        ? previous.filter((id) => id !== draftId)
        : [...previous, draftId]
    ));
  };

  const runReports = async () => {
    if (selectedReportDraftIds.length === 0) {
      toast.error('Select at least one completed draft');
      return;
    }

    try {
      setRunningReports(true);
      const responses = await Promise.all(
        selectedReportDraftIds.map((draftId) => apiClient.drafts.getById(draftId))
      );
      setReportDrafts(responses
        .filter((response) => response.success)
        .map((response) => response.data.draft));
    } catch (error) {
      console.error('Failed to run draft reports:', error);
      toast.error('Unable to load completed draft reports');
    } finally {
      setRunningReports(false);
    }
  };

  const confirmAssignUser = async () => {
    if (!teamToAssignUser || !selectedUserId) return;

    try {
      setAssigningUser(true);
      const response = await apiClient.teams.assignOwner(teamToAssignUser._id, selectedUserId);

      if (response.success) {
        toast.success('User assigned to team successfully');
        setShowAssignUserModal(false);
        setTeamToAssignUser(null);
        setSelectedUserId('');
        loadAdminData();
      }
    } catch (error) {
      console.error('Failed to assign user to team:', error);
      toast.error('Failed to assign user to team');
    } finally {
      setAssigningUser(false);
    }
  };

  if (loading) {
    return (
      <AdminContainer>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <LoadingSpinner size="lg" />
          </div>
        </Container>
      </AdminContainer>
    );
  }

  const activeDrafts = drafts.filter(draft => draft.status === 'active');
  const completedDrafts = drafts.filter(draft => draft.status === 'completed');
  const assignableTeams = assignTeamOrderIds
    .map((teamId) => orderedTeams.find((team) => team._id === teamId))
    .filter((team): team is Team => Boolean(team));
  const selectedOrderedTeamIds = assignTeamOrderIds.filter((teamId) => selectedTeamIds.includes(teamId));
  const selectedCompletedDrafts = completedDrafts.filter((draft) => selectedReportDraftIds.includes(draft._id));
  const reportPicks = reportDrafts.flatMap((draft) => (
    draft.picks
      .filter((pick) => !pick.isUndone)
      .map((pick) => ({
        ...pick,
        draftName: draft.name,
        teamName: typeof pick.teamId === 'string' ? 'Unknown team' : pick.teamId?.name || 'Unknown team'
      }))
  ));
  const totalReportSpend = reportPicks.reduce((total, pick) => total + pick.cost, 0);
  const positionCounts = reportPicks.reduce<Record<string, number>>((counts, pick) => {
    counts[pick.position] = (counts[pick.position] || 0) + 1;
    return counts;
  }, {});
  const teamSpending = reportPicks.reduce<Record<string, { name: string; spend: number; players: number }>>((teams, pick) => {
    const key = `${pick.draftName}-${pick.teamName}`;
    const team = teams[key] || { name: `${pick.teamName} (${pick.draftName})`, spend: 0, players: 0 };
    team.spend += pick.cost;
    team.players += 1;
    teams[key] = team;
    return teams;
  }, {});
  const topPicks = [...reportPicks].sort((left, right) => right.cost - left.cost).slice(0, 8);
  const topTeams = Object.values(teamSpending).sort((left, right) => right.spend - left.spend).slice(0, 8);

  return (
    <AdminContainer>
      <Container>
        <PageHeader>
          <PageTitle>Admin Dashboard</PageTitle>
          <PageSubtitle>
            Manage drafts, teams, and system settings
          </PageSubtitle>
        </PageHeader>

        <AdminGrid>
          <div>
            <SectionCard>
              <SectionTitle>
                <Settings size={24} />
                Quick Actions
              </SectionTitle>
              
              <ActionGrid>
                <ActionCard onClick={() => setShowCreateDraftModal(true)}>
                  <ActionIcon>
                    <Plus />
                  </ActionIcon>
                  <ActionTitle>Create Draft</ActionTitle>
                  <ActionDescription>Start a new fantasy draft</ActionDescription>
                </ActionCard>

                <ActionCard onClick={() => setShowCreateTeamModal(true)}>
                  <ActionIcon>
                    <UserPlus />
                  </ActionIcon>
                  <ActionTitle>Create Team</ActionTitle>
                  <ActionDescription>Add a new team to the league</ActionDescription>
                </ActionCard>

                <ActionCard onClick={syncing ? undefined : handleSyncPlayers} style={{ opacity: syncing ? 0.6 : 1, cursor: syncing ? 'not-allowed' : 'pointer' }}>
                  <ActionIcon>
                    {syncing ? <LoadingSpinner size="sm" /> : <Database />}
                  </ActionIcon>
                  <ActionTitle>{syncing ? 'Syncing...' : 'Sync Players'}</ActionTitle>
                  <ActionDescription>Update player data from SportsDataIO</ActionDescription>
                </ActionCard>

                <ActionCard onClick={() => window.location.href = '/admin/picks'}>
                  <ActionIcon>
                    <Trophy />
                  </ActionIcon>
                  <ActionTitle>Manage Picks</ActionTitle>
                  <ActionDescription>View and edit recent draft picks</ActionDescription>
                </ActionCard>

                <ActionCard onClick={() => window.location.href = '/reports'}>
                  <ActionIcon>
                    <BarChart3 />
                  </ActionIcon>
                  <ActionTitle>Reports</ActionTitle>
                  <ActionDescription>Analyze one or more completed drafts</ActionDescription>
                </ActionCard>
              </ActionGrid>
            </SectionCard>

            <SectionCard>
              <SectionTitle>
                <Trophy size={24} />
                Draft Management
              </SectionTitle>
              
              {drafts.length > 0 ? (
                <DraftsList>
                  {drafts.map(draft => (
                    <DraftItem key={draft._id}>
                      <DraftInfo>
                        <DraftName>{draft.name}</DraftName>
                        <DraftDetails>
                          <StatusBadge status={draft.status}>
                            {draft.status}
                          </StatusBadge>
                          {draft.teams.length} teams • Round {draft.currentRound}
                        </DraftDetails>
                      </DraftInfo>
                      
                      <DraftActions>
                        {draft.status === 'created' && draft.teams.length > 0 && (
                          <Button 
                            size="sm" 
                            onClick={() => handleStartDraft(draft._id)}
                          >
                            <Play size={16} />
                            Start
                          </Button>
                        )}
                        
                        {draft.status === 'created' && (
                          <Button 
                            size="sm" 
                            variant="secondary"
                            onClick={() => handleAssignTeams(draft)}
                          >
                            <Users size={16} />
                            Teams ({draft.teams.length})
                          </Button>
                        )}

                        {draft.status === 'completed' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => window.location.href = `/draft/${draft._id}/results`}
                          >
                            View Results
                          </Button>
                        )}

                        <Button 
                          size="sm" 
                          variant="danger"
                          onClick={() => handleDeleteDraft(draft)}
                        >
                          <Trash2 size={16} />
                          Delete
                        </Button>
                      </DraftActions>
                    </DraftItem>
                  ))}
                </DraftsList>
              ) : (
                <div style={{ textAlign: 'center', padding: spacing[8], color: colors.grey[400] }}>
                  <Trophy size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
                  <p>No drafts created yet</p>
                </div>
              )}
            </SectionCard>

            <SectionCard>
              <SectionTitle>
                <Users size={24} />
                Team Management
              </SectionTitle>

              <TeamOrderHeader>
                <TeamOrderHint>Drag teams to set draft order, then save.</TeamOrderHint>
                <Button
                  size="sm"
                  onClick={handleSaveTeamOrder}
                  disabled={!isTeamOrderDirty || savingTeamOrder}
                >
                  <Save size={16} />
                  {savingTeamOrder ? 'Saving...' : isTeamOrderDirty ? 'Save Order' : 'Order Saved'}
                </Button>
              </TeamOrderHeader>
              
              {teams.length > 0 ? (
                <DraftsList>
                  {orderedTeams.map((team, index) => (
                    <DraggableTeamItem
                      key={team._id}
                      draggable
                      isDragging={draggingTeamId === team._id}
                      isDragOver={dragOverTeamId === team._id}
                      onDragStart={() => handleTeamDragStart(team._id)}
                      onDragOver={(event) => handleTeamDragOver(event, team._id)}
                      onDrop={() => handleTeamDrop(team._id)}
                      onDragEnd={handleTeamDragEnd}
                    >
                      <DragHandle type="button" aria-label="Drag to reorder teams">
                        <GripVertical size={16} />
                      </DragHandle>
                      <TeamOrderControls>
                        <OrderButton
                          type="button"
                          aria-label={`Move ${team.name} up`}
                          disabled={index === 0}
                          onClick={() => moveTeamByDirection(team._id, 'up')}
                        >
                          <ChevronUp size={14} />
                        </OrderButton>
                        <OrderButton
                          type="button"
                          aria-label={`Move ${team.name} down`}
                          disabled={index === orderedTeams.length - 1}
                          onClick={() => moveTeamByDirection(team._id, 'down')}
                        >
                          <ChevronDown size={14} />
                        </OrderButton>
                      </TeamOrderControls>
                      <DraftInfo>
                        <DraftName>{team.name}</DraftName>
                        <DraftDetails>
                          Draft #{getTeamOrderValue(team)} • Team #{team.teamNumber}
                          {team.ownerId ? ` • Owner: ${team.ownerId.username || 'Unknown'}` : ' • No owner assigned'}
                        </DraftDetails>
                      </DraftInfo>
                      
                      <DraftActions>
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={() => handleAssignUser(team)}
                        >
                          <UserPlus size={16} />
                          {team.ownerId ? 'Change Owner' : 'Assign Owner'}
                        </Button>
                      </DraftActions>
                    </DraggableTeamItem>
                  ))}
                </DraftsList>
              ) : (
                <div style={{ textAlign: 'center', padding: spacing[8], color: colors.grey[400] }}>
                  <Users size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
                  <p>No teams created yet</p>
                </div>
              )}
            </SectionCard>
          </div>

          <div>
            <SectionCard>
              <SectionTitle>
                <Users size={24} />
                System Stats
              </SectionTitle>
              
              <StatsGrid>
                <StatItem>
                  <StatLabel>Total Drafts</StatLabel>
                  <StatValue>{drafts.length}</StatValue>
                </StatItem>
                
                <StatItem>
                  <StatLabel>Active Drafts</StatLabel>
                  <StatValue>{activeDrafts.length}</StatValue>
                </StatItem>
                
                <StatItem>
                  <StatLabel>Completed Drafts</StatLabel>
                  <StatValue>{completedDrafts.length}</StatValue>
                </StatItem>
                
                <StatItem>
                  <StatLabel>Total Teams</StatLabel>
                  <StatValue>{teams.length}</StatValue>
                </StatItem>
                
                <StatItem>
                  <StatLabel>Teams with Owners</StatLabel>
                  <StatValue>{teams.filter(team => team.ownerId).length}</StatValue>
                </StatItem>
              </StatsGrid>
            </SectionCard>
          </div>
        </AdminGrid>

        {/* Create Draft Modal */}
        <Modal isOpen={showReportsModal}>
          <ReportsModalContent>
            <ModalTitle>Completed Draft Reports</ModalTitle>
            {completedDrafts.length === 0 ? (
              <p style={{ color: colors.grey[400] }}>No completed drafts are available for reporting.</p>
            ) : (
              <>
                <ReportDraftList>
                  {completedDrafts.map((draft) => (
                    <ReportDraftOption key={draft._id} selected={selectedReportDraftIds.includes(draft._id)}>
                      <Checkbox
                        type="checkbox"
                        checked={selectedReportDraftIds.includes(draft._id)}
                        onChange={() => toggleReportDraft(draft._id)}
                      />
                      <ReportDraftName>{draft.name}</ReportDraftName>
                    </ReportDraftOption>
                  ))}
                </ReportDraftList>

                <Button onClick={runReports} disabled={runningReports || selectedReportDraftIds.length === 0} fullWidth>
                  {runningReports ? 'Running Reports...' : `Run Reports for ${selectedCompletedDrafts.length} Draft${selectedCompletedDrafts.length === 1 ? '' : 's'}`}
                </Button>

                {reportDrafts.length > 0 && (
                  <>
                    <ReportGrid>
                      <ReportMetric><ReportMetricLabel>Drafts Analyzed</ReportMetricLabel><ReportMetricValue>{reportDrafts.length}</ReportMetricValue></ReportMetric>
                      <ReportMetric><ReportMetricLabel>Players Drafted</ReportMetricLabel><ReportMetricValue>{reportPicks.length}</ReportMetricValue></ReportMetric>
                      <ReportMetric><ReportMetricLabel>Total Spend</ReportMetricLabel><ReportMetricValue>${totalReportSpend}</ReportMetricValue></ReportMetric>
                      <ReportMetric><ReportMetricLabel>Average Pick</ReportMetricLabel><ReportMetricValue>${reportPicks.length ? (totalReportSpend / reportPicks.length).toFixed(0) : 0}</ReportMetricValue></ReportMetric>
                    </ReportGrid>

                    <ReportSectionTitle>Position Mix</ReportSectionTitle>
                    <ReportList>
                      {Object.entries(positionCounts).sort(([left], [right]) => left.localeCompare(right)).map(([position, count]) => (
                        <ReportRow key={position}><ReportPrimary>{position}</ReportPrimary><span>{count} players</span></ReportRow>
                      ))}
                    </ReportList>

                    <ReportSectionTitle>Highest-Cost Picks</ReportSectionTitle>
                    <ReportList>
                      {topPicks.map((pick) => (
                        <ReportRow key={`${pick.draftName}-${pick.pickNumber}`}><ReportPrimary>{pick.playerName} · {pick.teamName} · {pick.draftName}</ReportPrimary><span>${pick.cost}</span></ReportRow>
                      ))}
                    </ReportList>

                    <ReportSectionTitle>Team Spending</ReportSectionTitle>
                    <ReportList>
                      {topTeams.map((team) => (
                        <ReportRow key={team.name}><ReportPrimary>{team.name}</ReportPrimary><span>${team.spend} · {team.players} players</span></ReportRow>
                      ))}
                    </ReportList>
                  </>
                )}
              </>
            )}

            <ModalActions>
              <Button variant="secondary" onClick={() => setShowReportsModal(false)}>Close</Button>
            </ModalActions>
          </ReportsModalContent>
        </Modal>

        <Modal isOpen={showCreateDraftModal}>
          <ModalContent>
            <ModalTitle>Create New Draft</ModalTitle>
            
            <Form onSubmit={handleCreateDraft}>
              <FormGroup>
                <Label htmlFor="draftName">Draft Name</Label>
                <Input
                  id="draftName"
                  type="text"
                  value={newDraftName}
                  onChange={(e) => setNewDraftName(e.target.value)}
                  placeholder="Enter draft name"
                  required
                />
              </FormGroup>
              
              <ModalActions>
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={() => setShowCreateDraftModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create Draft
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>

        {/* Create Team Modal */}
        <Modal isOpen={showCreateTeamModal}>
          <ModalContent>
            <ModalTitle>Create New Team</ModalTitle>
            
            <Form onSubmit={handleCreateTeam}>
              <FormGroup>
                <Label htmlFor="teamName">Team Name</Label>
                <Input
                  id="teamName"
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Enter team name"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="teamNumber">Team Number</Label>
                <Input
                  id="teamNumber"
                  type="number"
                  value={newTeamNumber}
                  onChange={(e) => setNewTeamNumber(e.target.value)}
                  placeholder="Enter team number (1-12)"
                  min="1"
                  max="12"
                  required
                />
              </FormGroup>
              
              <ModalActions>
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={() => setShowCreateTeamModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Create Team
                </Button>
              </ModalActions>
            </Form>
          </ModalContent>
        </Modal>

        {/* Delete Draft Confirmation Modal */}
        <Modal isOpen={showDeleteDraftModal}>
          <ModalContent>
            <ModalTitle>Delete Draft</ModalTitle>
            
            <div style={{ marginBottom: spacing[6] }}>
              <p style={{ color: colors.grey[300], marginBottom: spacing[4] }}>
                Are you sure you want to delete the draft "{draftToDelete?.name}"?
              </p>
              
              {draftToDelete?.status === 'active' || draftToDelete?.status === 'completed' ? (
                <div style={{ 
                  padding: spacing[4], 
                  background: colors.warning + '20', 
                  border: `1px solid ${colors.warning}`, 
                  borderRadius: spacing[2],
                  marginBottom: spacing[4]
                }}>
                  <p style={{ color: colors.warning, fontSize: typography.fontSize.sm, margin: 0 }}>
                    <strong>Warning:</strong> This draft has been started or completed. 
                    Deleting it will reset all player draft statuses and clear team rosters.
                  </p>
                </div>
              ) : null}
              
              <p style={{ color: colors.danger, fontSize: typography.fontSize.sm }}>
                This action cannot be undone.
              </p>
            </div>
            
            <ModalActions>
              <Button 
                type="button" 
                variant="secondary"
                onClick={() => {
                  setShowDeleteDraftModal(false);
                  setDraftToDelete(null);
                }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button 
                variant="danger"
                onClick={confirmDeleteDraft}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Draft'}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>

        {/* Assign User Modal */}
        <Modal isOpen={showAssignUserModal}>
          <ModalContent>
            <ModalTitle>Assign Owner to "{teamToAssignUser?.name}"</ModalTitle>
            
            <div style={{ marginBottom: spacing[4] }}>
              <p style={{ color: colors.grey[300], marginBottom: spacing[4] }}>
                Select a user to assign as the team owner:
              </p>
              
              {users.length > 0 ? (
                <div>
                  <FormGroup>
                    <Label htmlFor="userSelect">Select User</Label>
                    <select
                      id="userSelect"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      style={{
                        width: '100%',
                        padding: spacing[3],
                        background: colors.grey[800],
                        border: `1px solid ${colors.grey[600]}`,
                        borderRadius: spacing[2],
                        color: colors.white,
                        fontSize: typography.fontSize.base
                      }}
                    >
                      <option value="">-- Select a user --</option>
                      {users.map(user => (
                        <option key={user._id} value={user._id}>
                          {user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: spacing[8], 
                  color: colors.grey[400] 
                }}>
                  <Users size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
                  <p>No users available.</p>
                </div>
              )}
            </div>
            
            <ModalActions>
              <Button 
                type="button" 
                variant="secondary"
                onClick={() => {
                  setShowAssignUserModal(false);
                  setTeamToAssignUser(null);
                  setSelectedUserId('');
                }}
                disabled={assigningUser}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmAssignUser}
                disabled={assigningUser || !selectedUserId}
              >
                {assigningUser ? 'Assigning...' : 'Assign User'}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>

        {/* Assign Teams Modal */}
        <Modal isOpen={showAssignTeamsModal}>
          <ModalContent>
            <ModalTitle>Assign Teams to "{draftToAssignTeams?.name}"</ModalTitle>
            
            <div style={{ marginBottom: spacing[4] }}>
              <p style={{ color: colors.grey[300], marginBottom: spacing[4] }}>
                Select the teams that will participate in this draft:
              </p>
              
              {assignableTeams.length > 0 ? (
                <>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: spacing[3],
                    padding: spacing[3],
                    background: colors.grey[800],
                    borderRadius: spacing[2],
                    border: `1px solid ${colors.grey[700]}`
                  }}>
                    <div>
                      <p style={{ 
                        color: colors.white, 
                        marginBottom: spacing[1],
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.medium
                      }}>
                        Available Teams: {assignableTeams.length}
                      </p>
                      <p style={{ 
                        color: colors.grey[400], 
                        fontSize: typography.fontSize.xs 
                      }}>
                        Click to select/deselect teams, then drag rows to set this draft's order.
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (selectedTeamIds.length === assignableTeams.length) {
                          // If all are selected, deselect all
                          setSelectedTeamIds([]);
                        } else {
                          // Select all teams
                          setSelectedTeamIds(assignableTeams.map(team => team._id));
                        }
                      }}
                      title={
                        selectedTeamIds.length === assignableTeams.length
                          ? 'Deselect all teams' 
                          : 'Select all teams'
                      }
                    >
                      {selectedTeamIds.length === assignableTeams.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  
                                    <TeamsList>
                    {assignableTeams.map(team => {
                    const selectedPosition = selectedOrderedTeamIds.indexOf(team._id);
                    return (
                    <AssignableTeamItem
                      key={team._id}
                      draggable
                      selected={selectedTeamIds.includes(team._id)}
                      isDragging={assignDraggingTeamId === team._id}
                      isDragOver={assignDragOverTeamId === team._id}
                      onDragStart={() => handleAssignTeamDragStart(team._id)}
                      onDragOver={(event) => handleAssignTeamDragOver(event, team._id)}
                      onDrop={() => handleAssignTeamDrop(team._id)}
                      onDragEnd={handleAssignTeamDragEnd}
                      onClick={() => handleTeamSelection(team._id, !selectedTeamIds.includes(team._id))}
                    >
                      <Checkbox
                        type="checkbox"
                        checked={selectedTeamIds.includes(team._id)}
                        onChange={(e) => handleTeamSelection(team._id, e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <TeamInfo>
                        <TeamName>{team.name}</TeamName>
                        <TeamDetails>
                          {selectedPosition >= 0 ? `Draft #${selectedPosition + 1}` : 'Not in draft'} • Team #{team.teamNumber}
                          {team.ownerId ? ` • Owner: ${team.ownerId.username || 'Unknown'}` : ' • No owner assigned'}
                        </TeamDetails>
                      </TeamInfo>
                    </AssignableTeamItem>
                  );
                  })}
                </TeamsList>
                </>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: spacing[8], 
                  color: colors.grey[400] 
                }}>
                  <Users size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
                  <p>No teams available. Create teams first.</p>
                </div>
              )}
              
              <div style={{ 
                padding: spacing[3], 
                background: colors.grey[800], 
                borderRadius: spacing[2],
                marginTop: spacing[4]
              }}>
                <p style={{ color: colors.grey[300], fontSize: typography.fontSize.sm, margin: 0 }}>
                  Selected: {selectedTeamIds.length} teams
                </p>
                {selectedTeamIds.length > 0 && selectedTeamIds.length < 2 && (
                  <p style={{ color: colors.warning, fontSize: typography.fontSize.xs, margin: 0, marginTop: spacing[1] }}>
                    You need at least 2 teams to assign a draft.
                  </p>
                )}
              </div>
            </div>
            
            <ModalActions>
              <Button 
                type="button" 
                variant="secondary"
                onClick={() => {
                  setShowAssignTeamsModal(false);
                  setDraftToAssignTeams(null);
                  setSelectedTeamIds([]);
                  setAssignTeamOrderIds([]);
                  setAssignDraggingTeamId(null);
                  setAssignDragOverTeamId(null);
                }}
                disabled={assigningTeams}
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmAssignTeams}
                disabled={assigningTeams || selectedTeamIds.length < 2}
              >
                {assigningTeams ? 'Assigning...' : `Assign ${selectedTeamIds.length} Teams`}
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      </Container>
    </AdminContainer>
  );
};

export default AdminPage;
